
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";
import React, { useState } from "react";
 
const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNetraaEKSAgent() {
  return <RenderNetraaEKSAgent />;
}

export default function RenderNetraaEKSAgent() {
  const [showYaml, setShowYaml] = useState(false);
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
    
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
              <a className="hover:text-primary hover:underline" href="#">
                Netraa APM
              </a>
              <FiChevronRight />
              <span>Agent Setup Guide</span>
              <FiChevronRight />
              <span>Cloud</span>
              <FiChevronRight />
              <span>AWS</span>
              <FiChevronRight />
              <span className="text-[#1b140d] dark:text-white font-medium">
                EKS (EC2 Nodes)
              </span>
            </nav>
    
            {/* Intro */}
            <div className="mb-10 scroll-mt-24" id="overview">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
    
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  Monitoring AWS EKS (EC2 Nodes)
                </span>
              </h1>
    
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This guide explains how to monitor an Amazon EKS cluster running on EC2 worker nodes using Netraa APM.
              </p>
            </div>
    
            {/* Overview */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  <span className="material-symbols-outlined text-[24px]">insights</span>
                </span>
                Overview
              </h2>
    
              <p className="text-text-muted dark:text-gray-400">
                This setup enables collection of:
              </p>
    
              <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400 mt-3">
                <li>Node-level metrics (CPU, memory, disk)</li>
                <li>Pod and container metrics</li>
                <li>Network metrics</li>
                <li>Kubernetes resource metrics</li>
              </ul>
            </section>
    
            {/* Prerequisites */}
            <section className="mb-12 scroll-mt-24" id="prerequisites">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">check_circle</span>
                </span>
                Prerequisites
              </h2>
    
              <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400">
                <li>EKS cluster with EC2 node group</li>
                <li><code>kubectl</code> configured</li>
                <li>IAM permissions for EKS</li>
                <li>Nodes in <code>Ready</code> state</li>
              </ul>
            </section>
    
            {/* Configuration */}
    
    <section className="mb-12 scroll-mt-24" id="configuration">
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
          <span className="material-symbols-outlined text-[24px]">settings</span>
        </span>
        Configuration
      </h2>
    
      <div className="space-y-8">
    
        {/* Step 1 */}
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-semibold shadow-sm">
            1
          </div>
          <div className="flex-1">
            <p className="text-text-main dark:text-white font-medium mb-2">
              Open the provided file
            </p>
            <CodeBlock code={`nano aws-netraa.yaml`} />
    
            {/* Collapsible YAML */}
            <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    
              <button
                onClick={() => setShowYaml(!showYaml)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <span className="font-medium text-text-main dark:text-white">
                  {showYaml ? "Hide aws-netraa.yaml Configuration" : "Show aws-netraa.yaml Configuration"}
                </span>
    
                <span className="material-symbols-outlined text-gray-500">
                  {showYaml ? "expand_less" : "expand_more"}
                </span>
              </button>
    
              {showYaml && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <CodeBlock
                    code={`apiVersion: v1
    kind: ServiceAccount
    metadata:
      name: aws-otel-sa
      namespace: netraa
    
    ---
    kind: ClusterRole
    apiVersion: rbac.authorization.k8s.io/v1
    metadata:
      name: aoc-agent-role
    rules:
      - apiGroups: [""]
        resources: ["pods", "nodes", "endpoints"]
        verbs: ["list", "watch"]
      - apiGroups: ["apps"]
        resources: ["replicasets"]
        verbs: ["list", "watch"]
      - apiGroups: ["batch"]
        resources: ["jobs"]
        verbs: ["list", "watch"]
      - apiGroups: [""]
        resources: ["nodes/proxy"]
        verbs: ["get"]
      - apiGroups: [""]
        resources: ["nodes/stats", "configmaps", "events"]
        verbs: ["create", "get"]
      - apiGroups: [""]
        resources: ["configmaps"]
        resourceNames: ["otel-container-insight-clusterleader"]
        verbs: ["get", "update"]
      - apiGroups: ["coordination.k8s.io"]
        resources: ["leases"]
        verbs: ["create", "get", "update"]
    
    ---
    kind: ClusterRoleBinding
    apiVersion: rbac.authorization.k8s.io/v1
    metadata:
      name: aoc-agent-role-binding
    subjects:
      - kind: ServiceAccount
        name: aws-otel-sa
        namespace: netraa
    roleRef:
      kind: ClusterRole
      name: aoc-agent-role
      apiGroup: rbac.authorization.k8s.io
    
    ---
    apiVersion: v1
    kind: ConfigMap
    metadata:
      name: otel-agent-conf
      namespace: netraa
    data:
      otel-agent-config: |
        exporters:
          otlp:
            endpoint: netraaaop.ap2l.ai:11800
    
    ---
    apiVersion: apps/v1
    kind: DaemonSet
    metadata:
      name: aws-otel-eks-ci
      namespace: netraa
    spec:
      selector:
        matchLabels:
          name: aws-otel-eks-ci
      template:
        metadata:
          labels:
            name: aws-otel-eks-ci
        spec:
          containers:
            - name: aws-otel-collector
              image: amazon/aws-otel-collector:v0.23.0
          serviceAccountName: aws-otel-sa`}
                  />
                </div>
              )}
    
            </div>
          </div>
        </div>
    
        {/* Step 2 */}
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-semibold shadow-sm">
            2
          </div>
          <div className="flex-1">
            <p className="text-text-main dark:text-white font-medium mb-2">
              Update the default namespace
            </p>
            <CodeBlock code={`namespace: netraa\nChange to: your_namespace`} />
          </div>
        </div>
    
        {/* Step 3 */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white font-semibold shadow-sm flex items-center justify-center leading-none mt-1">
           3
          </div>
          <div className="flex-1">
            <p className="text-text-main dark:text-white font-medium mb-2">
              Update AOP Endpoint
            </p>
            <CodeBlock
              code={`data:\n  otel-agent-config:\n    exporters:\n      otlp:\n          endpoint: netraaaop.ap2l.ai:11800`}
            />
            <p className="text-sm text-text-muted dark:text-gray-400 mt-2">
              Replace with your actual AOP URL.
            </p>
          </div>
        </div>
    
      </div>
    </section>
    
          {/* Verify Deployment */}
    <section className="mb-12 scroll-mt-24" id="verify">
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
          <span className="material-symbols-outlined text-[24px]">verified</span>
        </span>
        Verify Deployment
      </h2>
    
      <div className="space-y-5">
        <CodeBlock code={`kubectl get pods -n your_namespace`} />
    
        <div>
          <p className="font-medium text-text-main dark:text-white mb-2">
            Expected Output
          </p>
          <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
            <li>One pod per node</li>
            <li>Status: Running</li>
          </ul>
        </div>
      </div>
    </section>
    
    {/* Metrics Collected */}
    <section className="mb-12 scroll-mt-24" id="metrics">
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 border border-purple-200 dark:border-purple-900/50">
          <span className="material-symbols-outlined text-[24px]">monitoring</span>
        </span>
        Metrics Collected
      </h2>
    
      <div className="grid md:grid-cols-3 gap-6">
    
        {/* Node Metrics */}
        <div className="p-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <p className="font-semibold text-text-main dark:text-white mb-3">
            Node Metrics
          </p>
          <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
            <li>CPU usage</li>
            <li>Memory usage</li>
            <li>Disk usage</li>
            <li>Network IO</li>
          </ul>
        </div>
    
        {/* Pod Metrics */}
        <div className="p-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <p className="font-semibold text-text-main dark:text-white mb-3">
            Pod Metrics
          </p>
          <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
            <li>CPU / Memory usage</li>
            <li>Pod lifecycle</li>
          </ul>
        </div>
    
        {/* Container Metrics */}
        <div className="p-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <p className="font-semibold text-text-main dark:text-white mb-3">
            Container Metrics
          </p>
          <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
            <li>CPU / Memory</li>
            <li>Restart count</li>
          </ul>
        </div>
    
      </div>
    </section>
    
    {/* Troubleshooting */}
    <section className="mb-12 scroll-mt-24" id="troubleshooting">
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 border border-red-200 dark:border-red-900/50">
          <span className="material-symbols-outlined text-[24px]">bug_report</span>
        </span>
        Troubleshooting
      </h2>
    
      <div className="space-y-6">
    
        {/* Issue 1 */}
        <div className="p-5 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="font-medium text-text-main dark:text-white mb-2">
            Pods not running
          </p>
          <CodeBlock code={`kubectl describe pod <pod-name> -n your_namespace`} />
        </div>
    
        {/* Issue 2 */}
        <div className="p-5 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="font-medium text-text-main dark:text-white mb-2">
            No metrics received
          </p>
          <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400 mb-3">
            <li>Check OTLP endpoint</li>
            <li>Check network connectivity</li>
          </ul>
          <CodeBlock code={`kubectl logs <pod-name> -n your_namespace`} />
        </div>
    
      </div>
    </section>
    
    {/* Notes */}
    <section className="mb-12">
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 border border-yellow-200 dark:border-yellow-900/50">
          <span className="material-symbols-outlined text-[24px]">note</span>
        </span>
        Notes
      </h2>
    
      <div className="p-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400">
          <li>DaemonSet works only on EC2 nodes, not Fargate</li>
          <li>Requires access to host filesystem (hostPath)</li>
          <li>Ensure IAM roles allow metric export</li>
        </ul>
      </div>
    </section>
    
    {/* Summary */}
    <section className="mb-12 scroll-mt-24" id="summary">
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
          <span className="material-symbols-outlined text-[24px]">table_view</span>
        </span>
        Summary
      </h2>
    
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
    
          <thead className="bg-gray-100 dark:bg-gray-900">
            <tr>
              <th className="px-5 py-3 text-left text-sm font-semibold text-text-main dark:text-white border-r border-gray-300 dark:border-gray-700">
                Component
              </th>
              <th className="px-5 py-3 text-left text-sm font-semibold text-text-main dark:text-white">
                Purpose
              </th>
            </tr>
          </thead>
    
          <tbody className="text-text-muted dark:text-gray-400 text-sm">
    
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
              <td className="px-5 py-3 border-t border-r border-gray-300 dark:border-gray-700 font-medium text-text-main dark:text-white">
                DaemonSet
              </td>
              <td className="px-5 py-3 border-t border-gray-300 dark:border-gray-700">
                Runs collector on every node
              </td>
            </tr>
    
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
              <td className="px-5 py-3 border-t border-r border-gray-300 dark:border-gray-700 font-medium text-text-main dark:text-white">
                ConfigMap
              </td>
              <td className="px-5 py-3 border-t border-gray-300 dark:border-gray-700">
                Defines OTEL pipeline
              </td>
            </tr>
    
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
              <td className="px-5 py-3 border-t border-r border-gray-300 dark:border-gray-700 font-medium text-text-main dark:text-white">
                RBAC
              </td>
              <td className="px-5 py-3 border-t border-gray-300 dark:border-gray-700">
                Grants access to cluster data
              </td>
            </tr>
    
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition">
              <td className="px-5 py-3 border-t border-r border-gray-300 dark:border-gray-700 font-medium text-text-main dark:text-white">
                Exporter
              </td>
              <td className="px-5 py-3 border-t border-gray-300 dark:border-gray-700">
                Sends metrics to NETRAA
              </td>
            </tr>
    
          </tbody>
        </table>
      </div>
    
      <p className="text-text-muted dark:text-gray-400 mt-4">
        This setup provides full observability for EKS clusters running on EC2 nodes with Netraa APM.
      </p>
    </section>
          </div>
        </main>
      </div>
    );
    }